import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {
  getAttributionLinesActiveForConnectedTeacher,
  getClipboardSchoolDiaryEntryEvent,
  getConnectedTeacherSchedules,
  getResourcesBySlot,
  getSchedule,
  getTeacherPlanningAllDayPersonalNotes,
  getTeacherPlanningByHoursByDates,
  selectedProfile,
  selectSchool,
  StoreState
} from '@redux/reducers';
import {School} from '@model/school';
import {combineLatest, Observable, of, throwError} from 'rxjs';
import {isConflictErrorWithIntegrityViolation, SharedFacadeService} from '@shared/shared-facade.service';
import {Schedule} from '@model/schedule';
import {Profile} from '@model/profile';
import {AttributionTimeEntry} from '@model/attribution-time-entry';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import {TeacherPlanningService} from '@services/teacher-planning.service';
import {
  teacherPlanningEventClipboardClear,
  teacherPlanningEventClipboardSet,
  teacherPlanningEventDeleted,
  teacherPlanningEventSaved,
  teacherPlanningEventsLoaded
} from '@redux/actions/teacher-planning.action';
import {DateTime} from 'luxon';
import {PlanningEventType, ResourceReservationEvent, SchoolDiaryEntryEvent, TeacherPlanningEvent, TeacherPlanningPersonalNoteEvent} from '@model/teacher-planning-event';
import {HttpErrorResponse} from '@angular/common/http';
import {ResourceService} from '@services/resource.service';
import {Resource} from '@model/resource';
import {resourcesReservationLoaded, resourceReservationUpdated} from '@redux/actions/resources.action';
import {Holiday} from '@model/holiday';
import {getDateString} from '@shared/util/date-util';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SchoolResourceYearlyReservation} from '@model/school-resource-yearly-reservation';
import {AttributionLineTeacherDetail} from "@model/attribution-line-teacher-detail";

@Injectable()
export class PlanningFacadeService {

  school$: Observable<School>;
  schedule$: Observable<Schedule>;
  profile$: Observable<Profile>;
  teacherSchedule$: Observable<{ [day: string]: { [Hour: string]: AttributionTimeEntry } }>;
  attributionLinesActiveForConnectedTeacher$: Observable<AttributionLineTeacherDetail[]>;
  allDayPersonalNoteEventsByDate$: Observable<{ [date: string]: TeacherPlanningPersonalNoteEvent; }>;
  planningEventsByHoursByDates$: Observable<{ [hour: string]: { [date: string]: TeacherPlanningEvent[] }; }>;
  resources$: Observable<Resource[]>;
  resourcesWithReservations$: Observable<Resource[]>;
  holidays$: Observable<Holiday[]>;
  clipboardSchoolDiaryEntryEvent$: Observable<SchoolDiaryEntryEvent>;
  schoolResourceYearlyReservations$: Observable<SchoolResourceYearlyReservation[]>;

  constructor(private _store: Store<StoreState>,
              private _sharedFacade: SharedFacadeService,
              private _resourceService: ResourceService,
              private _planningService: TeacherPlanningService,
              private _snackBar: MatSnackBar) {
    this.school$ = this._store.pipe(selectSchool);
    this.schedule$ = this._store.pipe(
      select(getSchedule)
    );
    this.profile$ = this._store.pipe(selectedProfile);
    this.teacherSchedule$ = this._store.pipe(
      select(getConnectedTeacherSchedules)
    );
    this.attributionLinesActiveForConnectedTeacher$ = this._store.pipe(
      select(getAttributionLinesActiveForConnectedTeacher)
    );
    this.allDayPersonalNoteEventsByDate$ = this._store.pipe(
      select(getTeacherPlanningAllDayPersonalNotes)
    );
    this.planningEventsByHoursByDates$ = this._store.pipe(
      select(getTeacherPlanningByHoursByDates)
    );
    this.resources$ = this._sharedFacade.resources$;
    this.holidays$ = combineLatest([this._sharedFacade.holidays$, this.school$]).pipe(
      map(([holidays, school]) => this._keepHolidaysApplyingToTheWholeSchool(holidays, school))
    );
    this.clipboardSchoolDiaryEntryEvent$ = this._store.pipe(select(getClipboardSchoolDiaryEntryEvent));
    this.resourcesWithReservations$ = this._store.pipe(select(getResourcesBySlot));
    this.schoolResourceYearlyReservations$ = this._sharedFacade.schoolResourceYearlyReservations$;
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }

  loadHolidaysForCurrentSchoolYear() {
    this._sharedFacade.loadHolidaysForCurrentSchoolYear();
  }

  loadConnectedTeacherSchedule() {
    this._sharedFacade.loadConnectedTeacherSchedule();
  }

  loadAttributionLinesForConnectedTeacher() {
    this._sharedFacade.loadAttributionLinesForConnectedTeacher();
  }

  loadPlanningEvents(startDate: DateTime, endDate: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._planningService.loadTeacherPlanningEvents(school.id, startDate, endDate))
    ).subscribe(events => this._store.dispatch(teacherPlanningEventsLoaded({planningEvents: events})));
  }

  savePlanningEvent(teacherPlanningEvent: TeacherPlanningEvent): Observable<TeacherPlanningEvent> {
    return this.school$.pipe(
      take(1),
      switchMap(school => this._planningService.saveTeacherPlanningEvent(school.id, teacherPlanningEvent)),
      catchError(err => this._handleSavePlanningEventError(err)),
      tap(res => {
        if (res != null) {
          this._store.dispatch(teacherPlanningEventSaved({planningEvent: res}))
        }
      })
    );
  }

  deletePlanningEvent(eventId: string): Observable<void> {
    return this.school$.pipe(
      take(1),
      switchMap(school => this._planningService.deleteTeacherPlanningEvent(school.id, eventId)),
      tap(() => this._store.dispatch(teacherPlanningEventDeleted({planningEventId : eventId})))
    );
  }

  private _keepHolidaysApplyingToTheWholeSchool(allHolidays: Holiday[], school: School): Holiday[] {
    const holidays = allHolidays.filter(holiday => holiday.classId == null);
    const schoolEstablishmentFases = (school && school.schoolEstablishments || []).map(establishment => establishment.fase);
    if (schoolEstablishmentFases.length === 0) {
      return holidays;
    }

    const fasesByDate = holidays.reduce((acc, holiday) => {
      const date = getDateString(holiday.date);
      acc[date] = acc[date] || new Set<number>();
      acc[date].add(Number(holiday.establishmentFase));
      return acc;
    }, {} as { [date: string]: Set<number> });

    return holidays.filter(holiday => holiday.establishmentFase == null
      || schoolEstablishmentFases.every(fase => fasesByDate[getDateString(holiday.date)].has(Number(fase))));
  }

  private _handleSavePlanningEventError(err: HttpErrorResponse) {
    function isEventNotFoundError(err: HttpErrorResponse) {
      return err.status === 404 && err.error && (<string>err.error.detail).indexOf('No event of type ') > -1;
    }

    return isConflictErrorWithIntegrityViolation(err) || isEventNotFoundError(err) ? of(null) : throwError(err);
  }

  loadSchoolResourcesWithReservation(date: DateTime, hour: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.getResource(school.id, true, school.activeSchoolYear, date, hour))
    ).subscribe((schoolResources) => this._store.dispatch(resourcesReservationLoaded({resources: schoolResources})));
  }

  reserveSchoolResource(reservation: Resource, date: DateTime, hour: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.addSchoolResourceReservation(school.id, reservation.id, date, hour, reservation.staffMemberId))
    ).subscribe(() => {
      this._store.dispatch(resourceReservationUpdated({resource: reservation}));
      let reservationEvent = {
        date: date,
        hour: hour,
        id: `${reservation.id}_${getDateString(date)}_${hour}`,
        reference: reservation.reference,
        type: PlanningEventType.RESOURCE_RESERVATION
      } as ResourceReservationEvent;
      this._store.dispatch(teacherPlanningEventSaved({planningEvent: reservationEvent}))
    });
  }

  unreserveSchoolResource(reservation: Resource, date: DateTime, hour: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.deleteSchoolResourceReservation(school.id, reservation.id, date, hour))
    ).subscribe(() => {
      this._store.dispatch(resourceReservationUpdated({resource: reservation}));
      this._store.dispatch(teacherPlanningEventDeleted({planningEventId : `${reservation.id}_${getDateString(date)}_${hour}`}));
    });
  }

  setClipboardSchoolDiaryEntryEvent(schoolDiaryEntryEvent: SchoolDiaryEntryEvent) {
    this._store.dispatch(teacherPlanningEventClipboardSet({planningEvent: schoolDiaryEntryEvent}));
    this._snackBar.open('Journal de classe copié', '', {duration: 2000});
  }

  clearClipboardSchoolDiaryEntryEvent() {
    this._store.dispatch(teacherPlanningEventClipboardClear());
  }

  loadResources() {
    this._sharedFacade.loadResources();
  }

  loadSchoolResourceYearlyReservations() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadSchoolResourceYearlyReservationsByStaffmember(profile.id));
  }
}
